import { useCallback, useEffect, useState } from 'react';
import type { Notification, NotificationListResponse } from 'shared';
import { api } from '../utils/api';

const POLL_INTERVAL_MS = 60_000;

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await api.get<NotificationListResponse>('/api/notifications');
      setNotifications(response.data);
      setUnreadCount(response.unread_count);
    } catch {
      // A failed poll is not worth surfacing: the next tick retries.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const markRead = useCallback(async (id: string) => {
    setNotifications((current) =>
      current.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    );
    setUnreadCount((count) => Math.max(0, count - 1));
    await api.put(`/api/notifications/${id}/read`, {}).catch(() => undefined);
  }, []);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications((current) => current.map((n) => ({ ...n, read_at: n.read_at ?? now })));
    setUnreadCount(0);
    await api.put('/api/notifications/read-all', {}).catch(() => undefined);
  }, []);

  return { notifications, unreadCount, loading, refresh, markRead, markAllRead };
}
